import React, { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AlertCircle, XCircle, TrendingDown, FileX } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export default function ProblemSection() {
  const container = useRef();
  const titleRef = useRef();
  const problemsRef = useRef();

  useGSAP(() => {
    gsap.from(titleRef.current, {
      opacity: 0,
      y: 40,
      duration: 0.8,
      scrollTrigger: {
        trigger: container.current,
        start: "top 75%",
        end: "top 25%",
        scrub: 0.5,
      }
    });

    gsap.from(problemsRef.current.children, {
      opacity: 0,
      y: 50,
      duration: 0.7,
      stagger: 0.15,
      scrollTrigger: {
        trigger: problemsRef.current,
        start: "top 80%",
        end: "top 30%",
        scrub: 0.5,
      }
    });
  }, { scope: container });

  const problems = [
    {
      icon: <XCircle size={32} />,
      title: "Generic, Template-Driven Responses",
      description: "Proposals that fail to differentiate your solution or articulate compelling win themes, resulting in low evaluation scores."
    },
    {
      icon: <FileX size={32} />,
      title: "Compliance Gaps and Disqualification Risk",
      description: "Missing requirements, incorrect formatting, or incomplete compliance matrices that put your entire submission at risk."
    },
    {
      icon: <TrendingDown size={32} />,
      title: "Inadequate Capture Strategy",
      description: "Reactive responses without pre-RFP positioning, customer engagement, or competitive intelligence that limits win probability."
    },
    {
      icon: <AlertCircle size={32} />,
      title: "Resource Constraints Under Pressure",
      description: "Overwhelmed internal teams struggling to manage multiple proposals simultaneously while maintaining quality and meeting deadlines."
    }
  ];

  return (
    <section id="problem" ref={container} className="section section-dark">
      <div className="section-content">
        <div className="section-header" ref={titleRef}>
          <div className="section-eyebrow">The Challenge</div>
          <h2 className="section-title">Why Government Proposals Fail</h2>
          <p className="section-description">
            Despite strong technical capabilities and competitive pricing, many organizations lose contracts due to preventable proposal weaknesses.
          </p>
        </div>

        <div ref={problemsRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px', marginTop: '60px' }}>
          {problems.map((problem, index) => (
            <div 
              key={index}
              style={{
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border-subtle)',
                padding: '32px',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{ color: 'var(--gold-primary)', marginBottom: '20px' }}>
                {problem.icon}
              </div>
              <h3 style={{ fontSize: '20px', marginBottom: '12px', color: 'var(--text-primary)' }}>
                {problem.title}
              </h3>
              <p style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                {problem.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
